import { useLiveQuery } from "dexie-react-hooks"

import { dexie } from "../../db"
import type { LiveQueryResult } from "../../types"
import { findOrderedSteps, type RealOrderedStep } from "../orderedSteps/orderedSteps"
import { findAllPhotosForSession, findPhotoById, getPendingPhotoDeletions, type PhotoSlim } from "./photos"

export function useAllPhotosForSession(sessionId: string): LiveQueryResult<PhotoSlim[]> {
  return useLiveQuery(async () => ({ data: await findAllPhotosForSession(sessionId), isLoading: false }), [sessionId], {
    data: undefined,
    isLoading: true,
  })
}

export function usePhotoById(photoId: string): LiveQueryResult<PhotoSlim | undefined> {
  return useLiveQuery(async () => ({ data: await findPhotoById(photoId), isLoading: false }), [photoId], {
    data: undefined,
    isLoading: true,
  })
}

export function usePhotosByStepType(
  sessionId: string,
  stepType: RealOrderedStep["stepType"],
): LiveQueryResult<PhotoSlim[]> {
  return useLiveQuery(
    async () => {
      const allSteps = await findOrderedSteps(sessionId)
      const stepNamesByStepType = allSteps.filter((s) => s.stepType === stepType).map((s) => s.name)

      const photos = await dexie.photos
        .where("sessionId")
        .equals(sessionId)
        .and((p) => !p.deleted && stepNamesByStepType.includes(p.type))
        .toArray()

      return { data: photos, isLoading: false }
    },
    [sessionId, stepType],
    { data: undefined, isLoading: true },
  )
}

/** Uploaded photos deleted locally that still need their deletion synced to the server */
export function usePendingPhotoDeletions(sessionId: string): LiveQueryResult<PhotoSlim[]> {
  return useLiveQuery(
    async () => ({ data: await getPendingPhotoDeletions(sessionId), isLoading: false }),
    [sessionId],
    { data: undefined, isLoading: true },
  )
}
